/**
 * Body-font picker. Lets us evaluate candidate sans-serif body fonts
 * side-by-side against Grifo headings without committing to a swap.
 *
 * Writes a `--font-body` CSS variable on <html> which Tailwind's
 * font-sans stack reads (see tailwind.config.ts). Persists the choice
 * in localStorage so a reload keeps the same font.
 *
 * Once a final pick is made, this picker can be removed and the
 * chosen family hard-coded into tailwind.config.ts.
 */
import { useEffect, useRef, useState } from 'react';
import { useViewportClamp } from '@/lib/useViewportClamp';
import { classNames } from '@/lib/format';

const STORAGE_KEY = 'kg-font-body';

type Option = { id: string; label: string; stack: string };

// IBM Plex Sans is the default body font for both the KG dashboard and
// the client portal, so it leads the list (and is the safety fallback in
// applyFont).
const DEFAULT_FONT_ID = 'plex';
const OPTIONS: Option[] = [
  { id: 'plex', label: 'IBM Plex', stack: '"IBM Plex Sans", Arial, sans-serif' },
  { id: 'walsheim', label: 'Walsheim', stack: '"GT Walsheim Pro", Arial, sans-serif' },
  { id: 'inter', label: 'Inter', stack: '"Inter", Arial, sans-serif' },
  { id: 'general', label: 'General', stack: '"General Sans", Arial, sans-serif' },
];

function applyFont(id: string) {
  const opt = OPTIONS.find((o) => o.id === id) ?? OPTIONS[0]!;
  document.documentElement.style.setProperty('--font-body', opt.stack);
}

export function initFontPreference() {
  const stored = (() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  })();
  if (stored && OPTIONS.some((o) => o.id === stored)) applyFont(stored);
  else applyFont(DEFAULT_FONT_ID);
}

export function FontPicker() {
  const [active, setActive] = useState<string>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) ?? DEFAULT_FONT_ID;
    } catch {
      return DEFAULT_FONT_ID;
    }
  });
  const [open, setOpen] = useState(false);
  const clamp = useViewportClamp<HTMLDivElement>(open);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    applyFont(active);
    try {
      localStorage.setItem(STORAGE_KEY, active);
    } catch {
      // ignore quota / private-mode errors
    }
  }, [active]);

  // Close on outside click / escape so it behaves like a normal menu.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs uppercase tracking-widest text-kg-grey-700 dark:text-kg-grey-300 hover:text-kg-black dark:hover:text-white transition-colors bg-kg-grey-100/70 dark:bg-white/[0.05]"
      >
        Fonts
        <span aria-hidden="true" className="text-[0.65rem] leading-none">▾</span>
      </button>
      {open && (
        <div
          role="menu"
          ref={clamp.ref}
          style={clamp.style}
          className="absolute right-0 top-full mt-1 z-20 min-w-[10rem] bg-white dark:bg-kg-grey-900 shadow-sm"
        >
          {OPTIONS.map((o) => (
            <button
              key={o.id}
              type="button"
              role="menuitemradio"
              aria-checked={active === o.id}
              onClick={() => {
                setActive(o.id);
                setOpen(false);
              }}
              className={classNames(
                'w-full text-left px-3 py-1.5 text-xs uppercase tracking-widest transition-colors',
                active === o.id
                  ? 'bg-kg-black text-white dark:bg-white dark:text-kg-black'
                  : 'text-kg-grey-700 dark:text-kg-grey-300 hover:bg-kg-grey-50 dark:hover:bg-kg-grey-800 hover:text-kg-black dark:hover:text-white',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
